import {z} from 'zod';
import dedent from 'dedent';
import {defineToolRegistryManifest} from '../../../types/base-tool-types';

const toolName = 'seo-generate-image-alt-text' as const;

/**
 * Schema for an image to generate alt text for
 */
export const imageForAltTextGenerationSchema = z.object({
  imageId: z
    .string()
    .describe(
      'Unique identifier for the image (can be elementId, assetId, or a composite key)'
    ),
  imageUrl: z
    .string()
    .describe('The URL of the image to generate alt text for'),
});

export type ImageForAltTextGeneration = z.infer<
  typeof imageForAltTextGenerationSchema
>;

/**
 * Schema for generated alt text result
 */
export const generatedAltTextResultSchema = z.object({
  imageId: z.string().describe('The image identifier from the input'),
  imageUrl: z.string().describe('The URL of the image'),
  altText: z
    .string()
    .nullable()
    .describe('The generated alt text, or null if generation failed'),
  error: z
    .string()
    .optional()
    .describe('Error message if alt text generation failed'),
});

export type GeneratedAltTextResult = z.infer<
  typeof generatedAltTextResultSchema
>;

/**
 * Input arguments for the generate-image-alt-text tool
 */
export const generateImageAltTextArgsSchema = z.object({
  siteId: z.string().describe('The ID of the site the images belong to'),
  images: z
    .array(imageForAltTextGenerationSchema)
    .describe('Array of images to generate alt text for'),
  localeTag: z
    .string()
    .optional()
    .describe(
      'Optional locale tag for localized alt text generation (e.g., "en-US", "fr-FR")'
    ),
  batchSize: z
    .number()
    .optional()
    .describe(
      'Number of images to process concurrently. Defaults to 20. Higher values may be faster but use more resources.'
    ),
});

export type GenerateImageAltTextArgs = z.infer<
  typeof generateImageAltTextArgsSchema
>;

/**
 * Return type for the generate-image-alt-text tool
 */
export const generateImageAltTextResultSchema = z.object({
  totalImages: z.number().describe('Total number of images processed'),
  successCount: z
    .number()
    .describe('Number of images with successfully generated alt text'),
  failureCount: z
    .number()
    .describe('Number of images that failed alt text generation'),
  results: z
    .array(generatedAltTextResultSchema)
    .describe('Array of results for each image'),
});

export type GenerateImageAltTextResult = z.infer<
  typeof generateImageAltTextResultSchema
>;

/**
 * Tool manifest for generating alt text for images using AI
 */
export const generateImageAltTextManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Generate image alt text',
  prettyStatus: {
    executed: 'Generated alt text for images',
    executing: 'Generating alt text for images',
    failed: 'Failed to generate alt text',
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Generates AI-powered alt text for a batch of images. This tool uses GPT-4 Vision
      to analyze images and create descriptive, accessible alt text.
      
      The tool validates images before processing (checking file type, size, and 
      accessibility) and handles AVIF conversion automatically.
      
      Supported image formats: JPEG, PNG, GIF, WebP, AVIF (converted to WebP).
      Maximum file size: 4MB per image.
      
      Returns generated alt text for each image, along with any errors encountered.
    `,
    inputSchema: generateImageAltTextArgsSchema,
  },
  type: 'server',
  permission: [{resource: 'SiteFile', action: 'manage'}],
  confirmBeforeExecuting: false,
  timeout: 300000, // 5 minutes - batch processing can take time
});
